// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { brandingApi } from '../api/brandingApi';
import type { BrandingConfig } from '../types/branding';
import { DEFAULT_BRANDING } from '../types/branding';

/**
 * Cache configuration
 */
const CACHE_VERSION = '1.0'; // Increment to invalidate all caches
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedBranding {
  data: BrandingConfig;
  timestamp: number;
  version: string;
  providerCode: string;
}

interface BrandingContextValue {
  branding: BrandingConfig;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  clearCache: () => void;
  setProviderCode: (providerCode: string) => void;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

interface BrandingProviderProps {
  children: ReactNode;
  providerCode?: string; // Optional: can be passed explicitly or resolved from subdomain
}

/**
 * Get the provider code from the current hostname subdomain.
 * E.g. "acme.provider.localhost.com" => "acme"
 */
function getProviderCodeFromHostname(): string {
  return window.location.hostname.split('.')[0];
}

/**
 * BrandingProvider - Manages branding configuration with localStorage caching
 *
 * Caching Strategy:
 * 1. Load from localStorage immediately (instant UI)
 * 2. Check if cache is valid (not expired, correct version, correct provider)
 * 3. If invalid or missing, fetch from API
 * 4. Update cache and UI with fresh data
 * 5. Apply custom colors to CSS variables
 *
 * Cache Key Format: `branding_provider_${providerCode}`
 * This ensures no clash with other app caches
 */
export function BrandingProvider({ children, providerCode: propProviderCode }: BrandingProviderProps) {
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentProviderCode, setCurrentProviderCode] = useState<string | null>(propProviderCode || null);

  /**
   * Get cache key for the current provider
   */
  const getCacheKey = (providerCode: string): string => {
    return `branding_provider_${providerCode}`;
  };

  /**
   * Load cached branding for current tenant (without knowing providerCode yet)
   * Scans localStorage for any valid cache entry matching the current subdomain
   *
   * IMPORTANT: This only loads from cache, does NOT trigger API calls
   */
  const loadCachedBrandingForTenant = useCallback((): void => {
    try {
      const subdomain = getProviderCodeFromHostname();
      const cacheKey = `branding_provider_${subdomain}`;
      const cached = localStorage.getItem(cacheKey);

      if (cached) {
        try {
          const cachedData: CachedBranding = JSON.parse(cached);

          // Validate cache
          const isExpired = Date.now() - cachedData.timestamp > CACHE_TTL_MS;
          const isWrongVersion = cachedData.version !== CACHE_VERSION;

          if (!isExpired && !isWrongVersion) {
            console.log(`[BrandingContext] Found cached branding for provider ${subdomain}`);
            setBranding(cachedData.data);
            applyColors(cachedData.data);
            setLoading(false);
            return;
          } else {
            // Remove expired/invalid cache
            localStorage.removeItem(cacheKey);
          }
        } catch (err) {
          console.error('[BrandingContext] Failed to parse cached branding:', err);
        }
      }

      console.log(`[BrandingContext] No valid cached branding found for provider ${subdomain}`);
      setLoading(false);
    } catch (err) {
      console.error('[BrandingContext] Error loading cached branding:', err);
      setLoading(false);
    }
  }, []);

  /**
   * Load branding from localStorage cache
   */
  const loadFromCache = (providerCode: string): BrandingConfig | null => {
    try {
      const cacheKey = getCacheKey(providerCode);
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        return null;
      }

      const cachedData: CachedBranding = JSON.parse(cached);

      // Validate cache
      const isExpired = Date.now() - cachedData.timestamp > CACHE_TTL_MS;
      const isWrongVersion = cachedData.version !== CACHE_VERSION;
      const isWrongProvider = cachedData.providerCode !== providerCode;

      if (isExpired || isWrongVersion || isWrongProvider) {
        // Invalid cache, remove it
        localStorage.removeItem(cacheKey);
        return null;
      }

      console.log(`[BrandingContext] Loaded branding from cache for provider ${providerCode}`);
      return cachedData.data;
    } catch (err) {
      console.error('[BrandingContext] Failed to load from cache:', err);
      return null;
    }
  };

  /**
   * Save branding to localStorage cache
   */
  const saveToCache = (providerCode: string, brandingData: BrandingConfig): void => {
    try {
      const cacheKey = getCacheKey(providerCode);
      const cachedData: CachedBranding = {
        data: brandingData,
        timestamp: Date.now(),
        version: CACHE_VERSION,
        providerCode
      };

      localStorage.setItem(cacheKey, JSON.stringify(cachedData));
      console.log(`[BrandingContext] Saved branding to cache for provider ${providerCode}`);
    } catch (err) {
      console.error('[BrandingContext] Failed to save to cache:', err);
    }
  };

  /**
   * Apply branding colors to CSS variables
   */
  const applyColors = (brandingData: BrandingConfig): void => {
    if (brandingData.colors?.primary) {
      document.documentElement.style.setProperty('--primary-color', brandingData.colors.primary);
    }
    if (brandingData.colors?.secondary) {
      document.documentElement.style.setProperty('--secondary-color', brandingData.colors.secondary);
    }
  };

  /**
   * Fetch branding from API
   */
  const fetchBranding = async (providerCode: string): Promise<BrandingConfig> => {
    try {
      console.log(`[BrandingContext] Fetching branding from API for provider ${providerCode}`);
      const brandingData = await brandingApi.getBrandingConfig(providerCode);

      // Save to cache
      saveToCache(providerCode, brandingData);

      // Apply colors
      applyColors(brandingData);

      return brandingData;
    } catch (err: unknown) {
      // Handle 401 (Unauthorized) specially - user is logged out
      const error = err as { response?: { status?: number } };
      if (error?.response?.status === 401) {
        console.warn('[BrandingContext] Unauthorized (401) - clearing cache and using default branding');
        // Clear cache to prevent retry loop
        const cacheKey = getCacheKey(providerCode);
        localStorage.removeItem(cacheKey);
        // Don't retry, just return default
        return DEFAULT_BRANDING;
      }

      console.error('[BrandingContext] Failed to fetch branding:', err);
      throw err;
    }
  };

  /**
   * Load branding (from cache or API)
   */
  const loadBranding = useCallback(async (providerCode: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Try loading from cache first
      const cachedBranding = loadFromCache(providerCode);

      if (cachedBranding) {
        // Use cached data immediately
        setBranding(cachedBranding);
        applyColors(cachedBranding);
        setLoading(false);

        // Fetch in background to check for updates (fire and forget)
        fetchBranding(providerCode)
          .then((freshBranding) => {
            // Only update if data changed
            if (JSON.stringify(freshBranding) !== JSON.stringify(cachedBranding)) {
              console.log('[BrandingContext] Branding updated from API');
              setBranding(freshBranding);
            }
          })
          .catch((err: unknown) => {
            // If 401, use default branding (user logged out)
            const error = err as { response?: { status?: number } };
            if (error?.response?.status === 401) {
              console.warn('[BrandingContext] Background fetch unauthorized - using default branding');
              setBranding(DEFAULT_BRANDING);
              applyColors(DEFAULT_BRANDING);
            } else {
              console.warn('[BrandingContext] Background fetch failed (using cache):', err);
            }
          });
      } else {
        // No cache, fetch from API
        const freshBranding = await fetchBranding(providerCode);
        setBranding(freshBranding);
        setLoading(false);
      }
    } catch (err: unknown) {
      // If 401, silently fall back to default (user logged out)
      const error = err as { response?: { status?: number } };
      if (error?.response?.status === 401) {
        console.warn('[BrandingContext] Load branding unauthorized - using default branding');
        setBranding(DEFAULT_BRANDING);
        applyColors(DEFAULT_BRANDING);
        setLoading(false);
        return;
      }

      console.error('[BrandingContext] Failed to load branding:', err);
      setError('Failed to load branding configuration');
      setBranding(DEFAULT_BRANDING);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Refetch branding (bypasses cache)
   */
  const refetch = async (): Promise<void> => {
    if (!currentProviderCode) {
      console.warn('[BrandingContext] Cannot refetch: No provider code');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const freshBranding = await fetchBranding(currentProviderCode);
      setBranding(freshBranding);
    } catch (err) {
      console.error('[BrandingContext] Refetch failed:', err);
      setError('Failed to refresh branding');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear cache for current provider
   */
  const clearCache = (): void => {
    if (!currentProviderCode) {
      console.warn('[BrandingContext] Cannot clear cache: No provider code');
      return;
    }

    const cacheKey = getCacheKey(currentProviderCode);
    localStorage.removeItem(cacheKey);
    console.log(`[BrandingContext] Cleared cache for provider ${currentProviderCode}`);
  };

  /**
   * Load cached branding immediately on mount (before providerCode is known)
   */
  useEffect(() => {
    loadCachedBrandingForTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Load branding when provider code changes
   */
  useEffect(() => {
    if (currentProviderCode) {
      loadBranding(currentProviderCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProviderCode]);

  const value: BrandingContextValue = {
    branding,
    loading,
    error,
    refetch,
    clearCache,
    setProviderCode: setCurrentProviderCode
  };

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

/**
 * Hook to access branding context
 */
export function useBranding(): BrandingContextValue {
  const context = useContext(BrandingContext);

  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }

  return context;
}
